<template>
  <div class="premium-message-detail" ref="detailRoot" @click="handleRootClick" :class="{ 'is-clickable': isWholeCardLinkEnabled }">
    <div class="user-info-wrapper" @click.stop="handleUserInfoClick">
      <img :src="message.user.icon_url" alt="ユーザアイコン" class="user-icon" />
      <div class="user-info">
        <div class="user-name">{{ message.user.name }}さん</div>
        <div class="user-profile">
          <span>{{ message.user.area }}</span>
          <span>{{ message.user.age_category }}</span>
          <span>{{ message.user.sex }}</span>
        </div>
      </div>
      <span v-if="message.event_group_id" class="event-badge">イベント限定</span>
    </div>
    <div class="message">
      <span class="message-text">{{ displayedText }}</span>
      <div v-if="message.is_read && hasOverflowText" class="message-actions">
        <button
          v-if="shouldShowMoreButton"
          class="show-more"
          type="button"
          @click.stop.prevent="showFullText"
        >
          <i class="bi bi-chevron-down"></i>
          <span>もっと見る</span>
        </button>
        <button
          v-else-if="shouldShowCloseButton"
          class="show-more"
          type="button"
          @click.stop.prevent="hideFullText"
        >
          <i class="bi bi-chevron-up"></i>
          <span>閉じる</span>
        </button>
      </div>
    </div>
    <div class="user-detail" v-if="shouldShowCloseButton">
      <hr />
      <div v-if="message.user.common_cards_count > 0">
        <div v-for="(card, i) in message.user.common_cards" :key="i" class="favorite">
          <img :src="card.thumbnail_image" alt="fav icon" class="favorite-icon" />
          <span class="favorite-text">{{ card.name }}</span>
        </div>
      </div>
      <div v-else class="other-common-cards">
        共通カード <span class="other-common-cards-count">0こ</span>
      </div>
      <div v-if="visibleOtherCommonCards" class="other-common-cards display-right">
        その他の共通カード <span class="other-common-cards-count">{{ otherCommonCardsCount }}こ</span>
      </div>
      <div class="introduce">
        {{ message.user.profile }}
      </div>
    </div>
    <div v-if="visibleActions" class="actions">
      <button class="btn btn-luna-outline btn-skip" @click.stop="onSkip">スキップ</button>
      <button class="btn btn-luna-search btn-thanks" @click.stop="onThanks">ありがとう</button>
    </div>
  </div>
</template>

<script>
const MAX_DISPLAY_COMMON_CARDS_COUNT = 3;

export default {
  props: {
    message: {
      type: Object,
      default: () => ({
        user: {
          id: 0,
          name: '',
          icon_url: '',
          area: '',
          age_category: '',
          sex: '',
          profile: '',
          common_cards: [],
          common_cards_count: 0,
        },
        message: ''
      })
    },
    isWholeCardLinkEnabled: {
      type: Boolean,
      default: false,
    },
    visibleActions: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      visibilityObserver: null,
      hasEmittedFullyVisible: false,
      isExpanded: false,
      textLimit: 100,
    }
  },
  computed: {
    currentPmid() {
      const pmid = this.$route && this.$route.params ? this.$route.params.pmid : undefined
      return pmid !== null ? String(pmid) : undefined
    },
    messageText() {
      return this.message && typeof this.message.message === 'string' ? this.message.message : ''
    },
    displayedText() {
      if (!this.message || !this.message.is_read) {
        return this.messageText
      }

      if (this.isExpanded || this.messageText.length <= this.textLimit) {
        return this.messageText
      }

      return `${this.messageText.slice(0, this.textLimit)}...`
    },
    hasOverflowText() {
      return !!(this.message && this.message.is_read && this.messageText.length > this.textLimit)
    },
    visibleOtherCommonCards() {
      return this.message.user.common_cards_count > MAX_DISPLAY_COMMON_CARDS_COUNT;
    },
    otherCommonCardsCount() {
      return this.message.user.common_cards_count - MAX_DISPLAY_COMMON_CARDS_COUNT;
    },
    shouldShowMoreButton() {
      return this.hasOverflowText && !this.isExpanded
    },
    shouldShowCloseButton() {
      return this.hasOverflowText && this.isExpanded
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.setupVisibilityObserver();
    });
  },
  beforeDestroy() {
    this.teardownVisibilityObserver();
  },
  watch: {
    message: {
      handler() {
        this.isExpanded = false;
        this.teardownVisibilityObserver();
        this.$nextTick(() => {
          this.setupVisibilityObserver();
        });
      },
      immediate: true,
      deep: true,
    }
  },
  methods: {
    handleRootClick() {
      if (!this.isWholeCardLinkEnabled) {
        return;
      }

      this.navigateToPremiumMessage();
    },
    handleUserInfoClick() {
      if (this.isWholeCardLinkEnabled) {
        this.navigateToPremiumMessage();
        return;
      }

      this.goToUser(this.message.user.id);
    },
    navigateToPremiumMessage() {
      if (this.$router) {
        try {
          this.$router.push('/premium/message');
        } catch (e) {
          this.$router.push({ path: '/premium/message' });
        }
      } else {
        window.location.href = '/premium/message';
      }
    },
    goToUser(userId) {
      // React 側のプロフィールを読み込むため、Vue Router を経由せずページ遷移する。
      window.location.href = `/user/show/${userId}?profile_source=premium_message_detail`
    },
    onSkip() {
      this.$emit('skip', this.message)
    },
    onThanks() {
      this.$emit('thanks', this.message)
    },
    showFullText() {
      if (!this.hasOverflowText) return
      this.isExpanded = true;
    },
    hideFullText() {
      this.isExpanded = false;
    },
    setupVisibilityObserver() {
      const target = this.$refs.detailRoot;
      if (!target || this.visibilityObserver) {
        return;
      }

      if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
        this.onFullyVisible();
        return;
      }

      this.visibilityObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === target && entry.isIntersecting && entry.intersectionRatio >= 1) {
            this.onFullyVisible();
          }
        });
      }, { threshold: 1.0 });

      this.visibilityObserver.observe(target);
    },
    teardownVisibilityObserver() {
      if (!this.visibilityObserver) {
        return;
      }

      this.visibilityObserver.disconnect();
      this.visibilityObserver = null;
    },
    async setMarkAsRead() {
      const MATCHING_STATUS_PREMIUM_MESSAGE_LIKE = 4;
      await axios.get(`/api/matching/view/set/${this.message.user.id}/${MATCHING_STATUS_PREMIUM_MESSAGE_LIKE}`);
    },
    async onFullyVisible() {
      if (this.hasEmittedFullyVisible || this.message.is_read) {
        return;
      }

      this.hasEmittedFullyVisible = true;
      await this.setMarkAsRead();
      this.$emit('fully-visible', this.message);
      this.teardownVisibilityObserver();
    }
  }
}
</script>

<style scoped>
.premium-message-detail {
  padding: 12px 16px;
  margin: 0 16px 16px;
  background: #fff;
  border-radius: 8px;
}

.premium-message-detail.is-clickable {
  cursor: pointer;
}

.user-info-wrapper {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.event-badge {
  display: inline-block;
  padding: 2px 8px;
  margin-left: auto;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 10px;
  font-size: 10px;
  font-weight: 700;
  color: #fff;
  white-space: nowrap;
}

.user-icon {
  width: 50px;
  height: 50px;
  border-radius: 25px;
}

.user-info {
  margin-left: 8px;
}

.user-name {
  font-size: 16px;
  font-weight: 700;
  line-height: 1.5;
}

.user-profile {
  color: #777681;
  font-size: 14px;
}

.message {
  margin-top: 16px;
  font-size: 12px;
  white-space: pre-wrap;
}

.message-text {
  white-space: pre-wrap;
  word-break: break-word;
}

.message-actions {
  display: flex;
  justify-content: center;
  margin-top: 8px;
}

.show-more {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0 auto;
  padding: 0;
  border: none;
  background: none;
  color: #ff2aa3;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.show-more:focus,
.show-more:active {
  outline: none;
  text-decoration: none;
}

.show-more .bi {
  -webkit-text-stroke: 1px;
}

.favorite {
  margin-bottom: 4px;
}

.favorite-icon {
  width: 24px;
  height: 24px;
  border-radius: 12px;
}

.favorite-text {
  margin-left: 8px;
  color: #777681;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
}

.other-common-cards {
  color: #777681;
  font-size: 12px;
}

.other-common-cards.display-right {
  text-align: right;
}

.other-common-cards-count {
  margin-left: 4px;
}

.introduce {
  margin-top: 16px;
  color: #777681;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
}

.actions {
  display: flex;
  gap: 12px;
  margin-top: 16px;
}

.btn {
  border-radius: 24px;
}

.btn-skip {
  flex: 0 0 auto;
  background-color: transparent;
  color: #ff2aa3;
  font-weight: 700;
  border: 2px #ff2aa3 solid;
}

.btn-thanks {
  flex: 1 1 auto;
  font-weight: 800;
}
</style>
